import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
    productSelectionCustomEventName,
    saveGainsightTrackingEvent,
} from 'shared/utils/gainsight'
import AddProductSelectionDialog from '../AddProductSelectionDialog'
import AddProductSelectionDialogEnhanced from '../AddProductSelectionDialogEnhanced'
import { DialogContext } from '../DialogContext'
import { DisplayTextFormatType, SelectionType } from '../ProductListingDialog.types'
import RemoveManufacturerWarning from '../Prompts/RemoveManufacturerWarning'
import RemoveMasterSpecWarning from '../Prompts/RemoveMasterSpecWarning'
import ProductListingCardPopper from './ProductListingCardPopper'

export interface ProductListingCardPopperContainerProps {
    listingId: string
    listingLevel: ListingLevel
    productType: string
    popperAnchorEl?: HTMLDivElement | null
    isProductDeprecated?: boolean
    isProductBOD?: boolean
    hasManufacturerOptions?: boolean
    hasSupportedOptions?: boolean
    isListingManufacturerProductType?: boolean
    isFlaggedForReview?: boolean
}

export interface AddProductToggleStates {
    isMfrProdPropsSelected: boolean
    isMfrProdOptionsSelected: boolean
    isMfrOnlyPropsSelected: boolean
    isMfrOnlyOptionsSelected: boolean
    isPropOnlyOptionsSelected: boolean
}

export const ProductListingCardPopperContainer: React.FC<ProductListingCardPopperContainerProps> = ({
    listingId,
    listingLevel,
    productType,
    popperAnchorEl,
    isProductDeprecated,
    isProductBOD,
    hasManufacturerOptions,
    hasSupportedOptions,
    isListingManufacturerProductType,
    isFlaggedForReview,
}) => {
    const {
        productSelected,
        projectProductListings,
        handleAddProductOption,
        handleRemoveProductOption,
        handleModifyProductOption,
        handleProductAddFlagForReview,
        handleProductRemoveFlagForReview,
    } = React.useContext(DialogContext)

    const Product = 'Product'
    const BOD = 'BOD'
    const FormatManufacturerAndProduct = 'ManufacturerAndProduct'
    const FormatManufacturer = 'Manufacturer'
    const FormatNone = 'None'

    const showEnhancedFeatureFlag =
        processEnv.REACT_APP_ShowProductSelectionEnhancement === '1'
    const [isDialogOpen, setDialogOpen] = useState(false)
    const [isEnhancedDialogOpen, setEnhancedDialogOpen] = useState(false)
    const [selectionType, setSelectionType] = useState<SelectionType>(Product)
    const selectionDisplayFormat = useRef<DisplayTextFormatType>(
        FormatManufacturerAndProduct
    )

    const [isRemoveMOWarningOpen, setIsRemoveMOWarningOpen] = useState(false)
    const [isRemoveMSWarningOpen, setIsRemoveMSWarningOpen] = useState(false)

    const [toggleStates, setToggleStates] = React.useState<
        AddProductToggleStates
    >({
        isMfrProdPropsSelected: true,
        isMfrProdOptionsSelected: false,
        isMfrOnlyPropsSelected: true,
        isMfrOnlyOptionsSelected: false,
        isPropOnlyOptionsSelected: false,
    })

    const isProductTypeInSelected = useMemo(
        () =>
            productSelected?.filter(
                (product) => product.productType === productType
            ),
        [productSelected, productType]
    )

    const selectedHaveManufacturerOptions = useMemo(
        () =>
            isProductTypeInSelected?.some(
                (product) => !!product.hasSupportedManufacturersOptions
            ),
        [isProductTypeInSelected]
    )

    const handleCloseAddProductSelection = useCallback(() => {
        setDialogOpen(false)
        setEnhancedDialogOpen(false)
    }, [setDialogOpen, setEnhancedDialogOpen])

    const triggerAddProduct = useCallback(
        (type: string) => () => {
            if (showEnhancedFeatureFlag) {
                handleAddProductOption?.(
                    listingId as string,
                    selectionType,
                    type === 'MO',
                    true,
                    selectionDisplayFormat.current ??
                    FormatManufacturerAndProduct
                )
            } else {
                handleAddProductOption?.(
                    listingId as string,
                    selectionType,
                    type === 'MO',
                    true,
                    'ManufacturerAndProduct'
                )
            }
            handleModifyProductOption?.(listingId as string, selectionType)
        },
        [
            handleAddProductOption,
            handleModifyProductOption,
            listingId,
            selectionType,
        ]
    )

    const handleAddProductToProject = () => {
        setSelectionType(Product)
        if (hasManufacturerOptions) {
            showEnhancedFeatureFlag
                ? setEnhancedDialogOpen(true)
                : setDialogOpen(true)
        } else if (
            (listingLevel === ListingLevel.PremiumMasterSpec ||
                listingLevel === ListingLevel.Premium ||
                listingLevel === ListingLevel.ProductListing) &&
            showEnhancedFeatureFlag
        ) {
            setEnhancedDialogOpen(true)
        } else {
            if (
                !!isProductTypeInSelected?.length &&
                selectedHaveManufacturerOptions
            ) {
                setIsRemoveMOWarningOpen(true)
                return
            }

            handleAddProductOption?.(
                listingId as string,
                Product,
                false,
                true,
                'ManufacturerAndProduct'
            )
            handleModifyProductOption?.(listingId as string, Product)
        }
    }

    const handleAddProductAsBOD = useCallback(() => {
        setSelectionType(BOD)
        if (hasManufacturerOptions) {
            setDialogOpen(true)
        } else {
            if (
                !!isProductTypeInSelected?.length &&
                selectedHaveManufacturerOptions
            ) {
                setIsRemoveMOWarningOpen(true)
                return
            }
            handleAddProductOption?.(
                listingId as string,
                BOD,
                false,
                true,
                'ManufacturerAndProduct'
            )
            handleModifyProductOption?.(listingId as string, BOD)
        }
    }, [
        hasManufacturerOptions,
        BOD,
        listingId,
        handleAddProductOption,
        handleModifyProductOption,
    ])

    const handleRemoveProduct = useCallback(() => {
        const isListingBOD = !projectProductListings
            ? false
            : projectProductListings?.find(
                (each) => each.listingId === listingId
            )?.isBOD ?? false
        handleRemoveProductOption?.(listingId as string, isListingBOD ?? false)
    }, [projectProductListings, listingId, handleRemoveProductOption])

    const handleMSOnlyClick = useCallback(() => {
        if (!!selectionType) {
            if (
                !!productSelected?.length &&
                !!isProductTypeInSelected?.length &&
                selectedHaveManufacturerOptions
            ) {
                setIsRemoveMOWarningOpen(true)
                return
            }

            handleAddProductOption?.(
                listingId as string,
                selectionType,
                false,
                true,
                'ManufacturerAndProduct'
            )
            handleModifyProductOption?.(listingId as string, selectionType)
        }
        setDialogOpen(false)
    }, [
        selectionType,
        listingId,
        handleAddProductOption,
        handleModifyProductOption,
        setDialogOpen,
    ])

    const handleMSandMFRClick = useCallback(() => {
        if (!!selectionType) {
            if (
                !!productSelected?.length &&
                !!isProductTypeInSelected?.length &&
                !selectedHaveManufacturerOptions
            ) {
                setIsRemoveMSWarningOpen(true)
                return
            }

            handleAddProductOption?.(
                listingId as string,
                selectionType,
                true,
                true,
                'ManufacturerAndProduct'
            )

            handleModifyProductOption?.(listingId as string, selectionType)
        }
        setDialogOpen(false)
    }, [
        selectionType,
        listingId,
        handleAddProductOption,
        handleModifyProductOption,
        setDialogOpen,
    ])

    //PSE handlers below
    const handleMfrAndProductClick = useCallback(
        (toggleStates: AddProductToggleStates) => {
            selectionDisplayFormat.current = FormatManufacturerAndProduct
            if (!!selectionType) {
                if (
                    !!productSelected?.length &&
                    !!isProductTypeInSelected?.length
                ) {
                    if (
                        !selectedHaveManufacturerOptions &&
                        toggleStates.isMfrProdOptionsSelected
                    ) {
                        setIsRemoveMSWarningOpen(true)
                        return
                    } else if (
                        selectedHaveManufacturerOptions &&
                        !toggleStates.isMfrProdOptionsSelected
                    ) {
                        setIsRemoveMOWarningOpen(true)
                        return
                    }
                }

                handleAddProductOption?.(
                    listingId as string,
                    selectionType,
                    toggleStates.isMfrProdOptionsSelected,
                    toggleStates.isMfrProdPropsSelected,
                    'ManufacturerAndProduct'
                )
                handleModifyProductOption?.(listingId as string, selectionType)

                saveGainsightTrackingEvent(productSelectionCustomEventName, {
                    ProductAddChoice: 'MfrProd',
                    PropertiesIncluded: toggleStates.isMfrProdPropsSelected,
                    MfrOptionsIncluded: toggleStates.isMfrProdOptionsSelected,
                })
            }

            setEnhancedDialogOpen(false)
        },
        [setEnhancedDialogOpen]
    )
    const handleMfrOnlyClick = useCallback(
        (toggleStates: AddProductToggleStates) => {
            selectionDisplayFormat.current = FormatManufacturer
            if (!!selectionType) {
                if (
                    !!productSelected?.length &&
                    !!isProductTypeInSelected?.length
                ) {
                    if (
                        !selectedHaveManufacturerOptions &&
                        toggleStates.isMfrOnlyOptionsSelected
                    ) {
                        setIsRemoveMSWarningOpen(true)
                        return
                    } else if (
                        selectedHaveManufacturerOptions &&
                        !toggleStates.isMfrOnlyOptionsSelected
                    ) {
                        setIsRemoveMOWarningOpen(true)
                        return
                    }
                }

                handleAddProductOption?.(
                    listingId as string,
                    selectionType,
                    toggleStates.isMfrOnlyOptionsSelected,
                    toggleStates.isMfrOnlyPropsSelected,
                    'Manufacturer'
                )
                handleModifyProductOption?.(listingId as string, selectionType)

                saveGainsightTrackingEvent(productSelectionCustomEventName, {
                    ProductAddChoice: 'MfrOnly',
                    PropertiesIncluded: toggleStates.isMfrOnlyPropsSelected,
                    MfrOptionsIncluded: toggleStates.isMfrOnlyOptionsSelected,
                })
            }

            setEnhancedDialogOpen(false)
        },
        [
            selectionType,
            listingId,
            handleAddProductOption,
            handleModifyProductOption,
            setEnhancedDialogOpen,
        ]
    )
    const handlePropOnlyClick = useCallback(
        (toggleStates: AddProductToggleStates) => {
            selectionDisplayFormat.current = FormatNone
            if (!!selectionType) {
                if (
                    !!productSelected?.length &&
                    !!isProductTypeInSelected?.length
                ) {
                    if (
                        !selectedHaveManufacturerOptions &&
                        toggleStates.isPropOnlyOptionsSelected
                    ) {
                        setIsRemoveMSWarningOpen(true)
                        return
                    } else if (
                        selectedHaveManufacturerOptions &&
                        !toggleStates.isPropOnlyOptionsSelected
                    ) {
                        setIsRemoveMOWarningOpen(true)
                        return
                    }
                }

                handleAddProductOption?.(
                    listingId as string,
                    selectionType,
                    toggleStates.isPropOnlyOptionsSelected,
                    true,
                    'None'
                )
                handleModifyProductOption?.(listingId as string, selectionType)

                saveGainsightTrackingEvent(productSelectionCustomEventName, {
                    ProductAddChoice: 'PropsOnly',
                    PropertiesIncluded: true,
                    MfrOptionsIncluded: toggleStates.isPropOnlyOptionsSelected,
                })
            }

            setEnhancedDialogOpen(false)
        },
        [setEnhancedDialogOpen]
    )

    const selectedProductInfo = useMemo(
        () => productSelected?.find((each) => each.listingId === listingId),
        [productSelected, listingId]
    )

    const closeWarningDialog = useCallback(
        (type: string) => () => {
            if (type === 'MO') {
                setIsRemoveMOWarningOpen(false)
            } else if (type === 'MS') {
                setIsRemoveMSWarningOpen(false)
            }
        },
        []
    )

    const onAddFlagForReview = useCallback(() => {
        const isListingBOD = !projectProductListings
            ? false
            : projectProductListings?.find(
                (each) => each.listingId === listingId
            )?.isBOD ?? false
        handleProductAddFlagForReview?.(listingId, isListingBOD)
    }, [projectProductListings, listingId, handleProductAddFlagForReview])

    const onRemoveFlagForReview = useCallback(() => {
        handleProductRemoveFlagForReview?.(listingId)
    }, [listingId, handleProductRemoveFlagForReview])

    return (
        <>
            <RemoveManufacturerWarning
                open={isRemoveMOWarningOpen}
                onCancel={closeWarningDialog('MO')}
                onSubmit={triggerAddProduct('MS')}
            />
            <RemoveMasterSpecWarning
                open={isRemoveMSWarningOpen}
                onCancel={closeWarningDialog('MS')}
                onSubmit={triggerAddProduct('MO')}
            />
            <ProductListingCardPopper
                listingId={listingId}
                isProductBOD={isProductBOD}
                popperAnchorEl={popperAnchorEl}
                isSelected={!!selectedProductInfo}
                isProductDeprecated={isProductDeprecated}
                selectionType={selectedProductInfo?.selectionType}
                isListingManufacturerProductType={
                    isListingManufacturerProductType
                }
                onAddProductToProject={handleAddProductToProject}
                onAddProductAsBOD={handleAddProductAsBOD}
                onRemoveProduct={handleRemoveProduct}
                isFlaggedForReview={isFlaggedForReview}
                onAddFlagForReview={onAddFlagForReview}
                onRemoveFlagForReview={onRemoveFlagForReview}
            />
            <AddProductSelectionDialog
                open={isDialogOpen}
                onMSOnlyClick={handleMSOnlyClick}
                onMSandMFRClick={handleMSandMFRClick}
                onCancel={handleCloseAddProductSelection}
            />

            <AddProductSelectionDialogEnhanced
                open={isEnhancedDialogOpen}
                listingType={listingLevel}
                hasManufacturerOptions={hasManufacturerOptions}
                onMfrAndProductClick={handleMfrAndProductClick}
                onMfrOnlyClick={handleMfrOnlyClick}
                onPropOnlyClick={handlePropOnlyClick}
                onCancel={handleCloseAddProductSelection}
            />
        </>
    )
}
